/**
 * ↔ **그림을 좌우로 뒤집는다** — 칸이 이어진 스트립이면 **칸마다 제자리에서.**
 *
 * ## 왜 필요한가
 * 우리 규칙은 「안 뒤집은 옆모습은 **오른쪽**을 본다」이다(웹은 왼쪽으로 갈 때만 뒤집는다 —
 * `WorldScene`: `setFlipX(face === "left")`). 그래서 왼쪽을 보고 그려진 그림이 들어오면
 * **방향이 정반대로** 움직인다 — 오른쪽으로 가면서 왼쪽을 본다. AI 가 그린 옆모습은
 * 반쯤 확률로 그렇게 나오고, 아무 에러도 안 난다.
 *
 * ## 🔴 스트립을 통째로 뒤집으면 안 된다
 * 통째로 뒤집으면 **칸 순서까지 거꾸로** 되어 걸음이 뒤로 감긴다. 칸마다 뒤집어야 한다.
 * (같은 이유가 `scripts/mirror-strip.mjs` 에도 있다 — 저장소에 든 그림용.)
 */

/** 뒤집을 칸들 — `x` 는 왼쪽 끝, `w` 는 칸 너비. 칸 순서는 그대로다. */
export interface MirrorSlice {
  x: number;
  w: number;
}

/**
 * 그림을 어떻게 나눠 뒤집을지. **캔버스 없이 판단할 수 있는 부분**을 떼어 둔 것 —
 * 칸 나누기가 틀리면 걸음이 뒤로 감기는데, 그건 눈으로만 보이고 에러가 안 난다.
 *
 * 정사각 칸이 가로로 이어져 있으면 그 칸 수대로, 아니면 한 장으로 본다.
 */
export function mirrorSlices(w: number, h: number): MirrorSlice[] {
  if (w <= 0 || h <= 0) return [];
  const frames = w % h === 0 ? w / h : 1;
  const fw = w / frames;
  return Array.from({ length: frames }, (_, i) => ({ x: i * fw, w: fw }));
}

export async function mirrorImageBlob(img: HTMLImageElement | HTMLCanvasElement): Promise<Blob | null> {
  const w = "naturalWidth" in img ? img.naturalWidth : img.width;
  const h = "naturalHeight" in img ? img.naturalHeight : img.height;
  const slices = mirrorSlices(w, h);
  if (slices.length === 0) return null;
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  for (const s of slices) {
    ctx.save();
    ctx.translate(s.x + s.w, 0);
    ctx.scale(-1, 1); // 이 칸 안에서만 좌우가 뒤집힌다 — 칸 자리는 그대로
    ctx.drawImage(img, s.x, 0, s.w, h, 0, 0, s.w, h);
    ctx.restore();
  }
  return await new Promise<Blob | null>((res) => out.toBlob(res, "image/webp", 0.92));
}

/**
 * ↔ **자리 하나를 뒤집어 다시 올린다.** 어느 화면에서 부르든 같은 길을 쓴다.
 *
 * 🔴 뒤집기가 두 곳에 있으면 한쪽만 고쳐진다. 예전엔 캐릭터 화면에만 있었고,
 * 그래서 배경·소품·굿즈 그림은 반대로 들어와도 관리자에서 손댈 방법이 없었다
 * (저장소 파일을 고치고 배포해야 했다 — 운영자가 못 하는 일이다).
 *
 * 🔴 **직전 그림을 `prev` 에 남긴다.** 그래야 [↩ 아까 그림으로]로 한 번에 되돌아간다.
 * 두 번 뒤집어 되돌리면 그림이 두 번 다시 구워져 조금씩 뭉개진다(webp 재인코딩).
 */
export async function flipAssetEntry(
  slotKey: string,
  url: string,
  deps: {
    loadImage: (u: string) => Promise<HTMLImageElement>;
    upload: (key: string, file: File) => Promise<string>;
  },
): Promise<{ url: string; prev: string }> {
  const blob = await mirrorImageBlob(await deps.loadImage(url));
  if (!blob) throw new Error("그림을 뒤집지 못했어요. 파일이 깨졌을 수 있어요.");
  const next = await deps.upload(slotKey, new File([blob], "flip.webp", { type: "image/webp" }));
  return { url: next, prev: url };
}
